/**
 * BeneficiariesMini — the dashboard's beneficiaries card.
 *
 * ⚠ AFS-GRADING finding 8 (E2E 2026-08-22): this card said "No beneficiaries on
 * file yet" while `GET /v1/trusts/{id}/beneficiaries` was returning two ACTIVE
 * beneficiaries. It was driven by `useBeneficialOwnership` filtered to
 * `category === 'beneficiary'` — the TPCA s1 beneficial-ownership register,
 * which is a DIFFERENT register from the trust's own beneficiary roster:
 *
 *   · BENEFICIARIES (`beneficiaries` container) — the trust DEED's register:
 *     who benefits, their relationship and share. Written by the beneficiary
 *     screens and by document ingestion. This is what the card is about, and
 *     what its "Manage →" link opens.
 *   · BENEFICIAL OWNERSHIP (`beneficial_ownership`) — the FIC/TPCA s1
 *     look-through record lodged with the Master. A trust can (and normally
 *     does, before any ID is uploaded) have beneficiaries with NO BO rows at
 *     all — which is exactly the state that read as "none on file".
 *
 * Same repoint the TrusteesCard already took (grader-r3 finding 4: count the
 * canonical roster, not BO records). The card now reads the beneficiary
 * register, and — because a person can be known ONLY from an uploaded ID / BO
 * register and not yet be in the deed register — MERGES in any BO row
 * categorised `beneficiary` that the register does not already name. Both come
 * back from ONE request (`includeOwnership: true`), so the merge costs no extra
 * round-trip and the two sources can never disagree about which trust they are
 * describing.
 *
 * Amounts aren't on either record; the column stays a dash (distribution
 * amounts live on resolutions).
 */

import { Link } from '@tanstack/react-router';
import { displayPersonName } from '../../lib/format/person-name';
import { useTrusts, useTrustBeneficiaries } from '../../lib/api';
import type { BeneficiaryRow, BoOwnerSummary } from '../../lib/api';
import { mapError } from '../../lib/error-messages';
import { beneficiaryRelationshipLabel } from '../../lib/labels';
import { initialsOf } from '../../lib/format/initials';

interface AvatarProps {
  initials: string;
  color: string;
  size?: number;
}

function Avatar({ initials, color, size = 36 }: AvatarProps) {
  return (
    <div
      className={'avatar ' + color}
      style={{ width: size, height: size, fontSize: size <= 30 ? 11 : 13 }}
    >
      {initials}
    </div>
  );
}

function colorOf(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const palette = ['brass', 'teal-soft', 'ink', ''];
  return palette[Math.abs(h) % palette.length] ?? 'brass';
}

/** One row of the card, whichever register named the person. */
export interface BeneficiaryCardRow {
  id: string;
  name: string;
  /** Sub-line: the deed relationship, else the BO record's own description. */
  detail: string;
  /** Which register named this person — drives nothing visual today, but keeps
   *  the merge honest and assertable. */
  source: 'register' | 'ownership';
}

/** Name fold for the merge — case + whitespace only. Deliberately NOT fuzzy: a
 *  fiduciary card must never silently collapse two different people. */
function nameKey(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Merge the trust's beneficiary REGISTER with the beneficiary-categorised rows
 * of the BO register — register first, de-duplicated by folded name. PURE +
 * exported so the merge is unit-tested without rendering.
 *
 * Archived register rows are dropped (the card shows who currently benefits);
 * a `deceased` beneficiary is KEPT and labelled, because the register keeps its
 * history and the trustees reading this card are the ones who need to know.
 */
export function mergeBeneficiarySources(
  register: readonly BeneficiaryRow[],
  owners: readonly BoOwnerSummary[],
): BeneficiaryCardRow[] {
  const out: BeneficiaryCardRow[] = [];
  const seen = new Set<string>();
  for (const b of register) {
    if (b.status !== 'active') continue;
    seen.add(nameKey(b.displayName));
    const deceased = b.lifeStatus === 'deceased';
    const detail = [
      beneficiaryRelationshipLabel(b.relationship) || (b.kind === 'class' ? 'Class of beneficiaries' : 'Beneficiary'),
      deceased ? (b.deceasedOn ? `Deceased ${b.deceasedOn}` : 'Deceased') : null,
    ]
      .filter((p): p is string => !!p)
      .join(' · ');
    out.push({ id: b.id, name: displayPersonName(b.displayName), detail, source: 'register' });
  }
  for (const o of owners) {
    if (o.category !== 'beneficiary') continue;
    if (seen.has(nameKey(o.fullName))) continue;
    seen.add(nameKey(o.fullName));
    out.push({
      id: o.id,
      name: o.fullName,
      detail:
        (o.ownerType === 'juristic-person' ? 'Juristic person' : 'Natural person') +
        (o.tpcaSubClause ? ` · s1(${o.tpcaSubClause})` : ''),
      source: 'ownership',
    });
  }
  return out;
}

export function BeneficiariesMini() {
  const trustsQ = useTrusts();
  const trustId = trustsQ.data?.trusts[0]?.id;
  // ONE request for both registers — see the file header.
  const benQ = useTrustBeneficiaries({ trustId, includeOwnership: true });

  const beneficiaries = mergeBeneficiarySources(
    benQ.data?.beneficiaries ?? [],
    benQ.data?.beneficialOwners ?? [],
  );

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <div className="eyebrow">Provided for</div>
          <h2 className="card-title">Beneficiaries</h2>
        </div>
        {trustId && (
          <Link
            to={`/app/trusts/${encodeURIComponent(trustId)}/beneficiaries` as never}
            className="card-link"
            style={{ textDecoration: 'none' }}
          >
            Manage →
          </Link>
        )}
      </div>
      {benQ.isLoading ? (
        <p style={{ color: 'var(--ink-3)', fontSize: 13 }} aria-live="polite">
          Loading beneficiaries on this trust&hellip;
        </p>
      ) : trustsQ.isError || benQ.isError ? (
        // A fetch failure must read as an ERROR, not "no beneficiaries" — the
        // empty-state copy below would silently lie about the register.
        <div role="alert" data-testid="beneficiaries-error">
          <p style={{ color: 'var(--negative)', fontSize: 13, lineHeight: 1.55, margin: '0 0 8px' }}>
            Couldn&rsquo;t load beneficiaries. {mapError(trustsQ.error ?? benQ.error, 'load')}
          </p>
          <button
            type="button"
            onClick={() => {
              if (trustsQ.isError) void trustsQ.refetch();
              if (benQ.isError) void benQ.refetch();
            }}
            style={{
              background: 'transparent',
              color: 'var(--primary-700)',
              border: '1px solid var(--primary-200, #b8d4ce)',
              padding: '5px 14px',
              borderRadius: 8,
              fontSize: 12.5,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      ) : beneficiaries.length === 0 ? (
        <p style={{ color: 'var(--ink-3)', fontSize: 13, lineHeight: 1.55 }}>
          No beneficiaries on file yet. Add the first one to anchor
          the trust&rsquo;s distribution structure.
        </p>
      ) : (
        <div className="ben-list">
          {beneficiaries.slice(0, 6).map((b) => (
            <div key={b.id} className="ben-row">
              <Avatar initials={initialsOf(b.name)} color={colorOf(b.id)} size={36} />
              <div>
                <div className="ben-name">{displayPersonName(b.name)}</div>
                <div className="ben-rel">{b.detail}</div>
              </div>
              <div className="ben-amt" style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
                {/* Distribution amounts live on resolutions, not the register. */}
                Not valued
              </div>
            </div>
          ))}
          {beneficiaries.length > 6 && (
            <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 4 }}>
              + {beneficiaries.length - 6} more
            </div>
          )}
        </div>
      )}
    </div>
  );
}
